/**
 * PWA helpers — service-worker registration + install-prompt event bus.
 *
 * The service worker is registered as soon as the app boots (production or
 * dev, since the preview URL is HTTPS). We also stash the browser's
 * `beforeinstallprompt` event on a module-level singleton so any component
 * can subscribe without racing against the initial event fire (which happens
 * once, very early, and is easy to miss with pure React hooks).
 */

let deferredPrompt = null;
const listeners = new Set();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Chrome/Edge fires this once when the app is eligible. We must call
    // preventDefault to keep the event around and call prompt() ourselves later.
    e.preventDefault();
    deferredPrompt = e;
    listeners.forEach((fn) => {
      try { fn(e); } catch (_err) { /* silent — one bad listener shouldn't kill others */ }
    });
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    listeners.forEach((fn) => {
      try { fn(null); } catch (_err) { /* silent */ }
    });
  });
}

export function subscribeInstallPrompt(fn) {
  listeners.add(fn);
  // Fire immediately if we already captured the event before the component mounted.
  if (deferredPrompt) {
    try { fn(deferredPrompt); } catch (_err) { /* silent */ }
  }
  return () => listeners.delete(fn);
}

export async function triggerInstall() {
  if (!deferredPrompt) return { outcome: "unavailable" };
  try {
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    // The event can only be used once per page load. Clear it so we don't
    // re-offer install after the user has already decided.
    deferredPrompt = null;
    listeners.forEach((fn) => { try { fn(null); } catch (_err) { /* silent */ } });
    return { outcome: choice.outcome || "dismissed" };
  } catch (_e) {
    return { outcome: "error" };
  }
}

export function isStandaloneDisplay() {
  if (typeof window === "undefined") return false;
  const mql = window.matchMedia && window.matchMedia("(display-mode: standalone)");
  return (mql && mql.matches) || window.navigator.standalone === true;
}

// ------------------- iter125: Web Push helpers -------------------
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

function urlBase64ToUint8Array(base64String) {
  // W3C Push Notifications spec requires the applicationServerKey to be
  // supplied as a Uint8Array of the raw public key bytes. Browsers give
  // us the key url-safe base64 encoded (no padding), so decode it here.
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window &&
    !!VAPID_PUBLIC_KEY
  );
}

export function pushPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission;
}

async function _postToApi(path, body) {
  // Lightweight fetch so this module stays free of the axios interceptor
  // chain (which pulls in the auth context and other UI-only deps).
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const headers = { "Content-Type": "application/json" };
  const token = (() => {
    try { return localStorage.getItem("rl_access") || null; } catch { return null; }
  })();
  if (token) headers.Authorization = `Bearer ${token}`;
  await fetch(`${base}/api${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    credentials: "include",
  });
}

export async function ensurePushSubscription() {
  // Returns {status: "granted"|"denied"|"unsupported"|"error"}. Idempotent —
  // safe to call every launcher load; PushManager.subscribe() dedupes.
  if (!pushSupported()) return { status: "unsupported" };
  try {
    const reg = await navigator.serviceWorker.ready;
    let perm = Notification.permission;
    if (perm === "default") perm = await Notification.requestPermission();
    if (perm !== "granted") return { status: perm };
    const existing = await reg.pushManager.getSubscription();
    const sub = existing || (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    }));
    const json = sub.toJSON();
    await _postToApi("/push/subscribe", {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 400) : null,
    });
    return { status: "granted" };
  } catch (e) {
    return { status: "error", error: e };
  }
}

export async function unsubscribePush() {
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const json = sub.toJSON();
    await sub.unsubscribe();
    await _postToApi("/push/unsubscribe", { endpoint: json.endpoint });
  } catch (_e) { /* silent */ }
}

export function isIOSSafari() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIOS && isSafari;
}

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  // Register lazily so it doesn't block first paint.
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .catch(() => { /* silent — a failed SW should never break the app */ });
  });
}
